import React, { useState, useEffect, useContext, } from 'react'
import { Pagination, Row, Col, Space, Input, Button, Empty, Skeleton, Typography, Spin, } from 'antd';
import { SyncOutlined, SearchOutlined, UserAddOutlined, } from '@ant-design/icons';
import { NocListItem } from '../NocListItem/NocListItem.component';
import { NocListHeader } from '../NocListHeader/NocListHeader.component';
import { Modal } from '../../../UI/Modal/Modal.component';
import { AddNewCustomerForm } from '../NocListHeader/AddNewCustomerForm.component';
import { requestLocations } from '../../../../services/UserService';
import { useApp } from '../../../../context/app-context';
import { NocContext } from '../../../../context/noc-context';
import { Restricted } from '../../../UI/Restricted.component';
import './NocList.css';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const { Title, Text } = Typography;

export const NocList = ({setCurrentNocItem, ...props}) => {

    const {dispatch, state: {user}} = useApp();

    const [nocState, dispatchNoc ] = useContext(NocContext);
    const { nocList, totalRecords } = nocState;

    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({current: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE});
    const [displayLastPage, setDisplayLastPage] = useState(false);
    const [displayPreviousPage, setDisplayPreviousPage] = useState(false);
    const [totalFilteredResults, setTotalFilteredResults] = useState(0);
    const [displayAddNewCustomer, setDisplayAddNewCustomer] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);

    const [refreshData, setRefreshData] = useState(false);
    const [fullRefreshTriggered, setFullRefreshTriggered] = useState(false);
    const [displaySuccessMessage, setDisplaySuccessMessage] = useState(false);

    useEffect(() => {
        setRefreshData({triggered: true, fullRefresh: true})
    }, [])

    useEffect(() => {
        if(displayAddNewCustomer){
            setDisplaySuccessMessage({});
        }
    }, [displayAddNewCustomer])

    const renderNocItem = () => {
        return nocList.map((item, index) => <NocListItem key={index} dataSource={item} refreshHandler={refreshHandler} />);
    };
    
    const refreshHandler = () => {
        setFullRefreshTriggered(true);
        setRefreshData({triggered: true, fullRefresh: true})
        dispatchNoc({
            type: 'START'
        })
    }

    const onShowSizeChange = (current, pageSize) => {
        const change = {
            current: current,
            pageSize: pageSize,
        };

        dispatchNoc({
            type: 'SET_PAGE_SIZE',
            payload: pageSize,
        })
        
        setPagination(change);
        setDisplayLastPage(false);
    };

    const onChange = (page, pageSize) => {
        const change = {
            current: page, 
            pageSize: pageSize,
        };
        window.scrollTo(0,0);
        setPagination(change);
        setDisplayLastPage(false);
        setRefreshData({triggered: true, fullRefresh: false})
    };

    useEffect(() => {
        if(displayLastPage){
            const change = {
                current: Math.ceil((totalRecords + 1)/pagination.pageSize), 
                pageSize: pagination.pageSize,
            }
            window.scrollTo(0,0);
            setPagination(change);
        }
    }, [displayLastPage])

    useEffect(() => {
        if(displayPreviousPage) {
            const change = {
                current: pagination.current - 1, 
                pageSize: pagination.pageSize,
            };
            setPagination(change);
            setDisplayPreviousPage(false);
        }
    }, [displayPreviousPage])

    useEffect(() => {
        if(fullRefreshTriggered){
            setRefreshData({triggered: true, fullRefresh: true});
        } else {
            setRefreshData({triggered: true, fullRefresh: false});
        }
    }, [search, fullRefreshTriggered])

    useEffect(() => {
        if(refreshData.triggered){
            handleFetch(refreshData.fullRefresh);
            setRefreshData({triggered: false, fullRefresh: false});
        }

        dispatchNoc({
            type: 'SET_PAGE_SIZE',
            payload: pagination.pageSize,
        })
    
        setDisplayLastPage(false);
    }, [refreshData, search, totalRecords, pagination.current, pagination.pageSize, dispatchNoc, displayLastPage ]);

    const handleFetch = (fullRefresh) => {
        if(!displayLastPage){
            setPagination({current: pagination.current, pageSize: pagination.pageSize});
        }

        let params = { limit: pagination.pageSize, page : pagination.current - 1, filter: search}            

        if(fullRefresh){
            setLoadingModal(true);
            setSearch('');
            params = {...params, filter: ''}
            const minDuration = 2000;
            const fetchLocationsPromise = requestLocations(params);
            const timeout = new Promise (resolve => setTimeout(resolve, minDuration));

            Promise.all([fetchLocationsPromise, timeout]).then(([response]) => {
                if(response.passwordReset) {
                    user['passwordReset'] = true;
                    dispatch({type: 'update-user', payload: user})
                } else {
                    dispatchNoc({
                        type: 'SET_NOC_LIST',
                        payload: response[0],
                    })
                    dispatchNoc({
                        type: 'SET_TOTAL_RECORDS', 
                        payload: response[2],
                    })
                    clearTimeout(timeout);
                    setLoadingModal(false);
                    setFullRefreshTriggered(false);
                }
            });    
        } else {
            requestLocations(params).then(function(response){
                if(response.passwordReset) {
                    user['passwordReset'] = true;
                    dispatch({type: 'update-user', payload: user})
                } else {
                    dispatchNoc({
                        type: 'SET_NOC_LIST',
                        payload: response[0],
                    })
                    if(!search){
                        dispatchNoc({
                            type: 'SET_TOTAL_RECORDS', 
                            payload: response[2],
                        })
                    } else {
                        setTotalFilteredResults(response[2]);
                    }
                }
            });
        }
    }

    useEffect(() => {
        if(nocList?.length === 0 && totalRecords > 0) {
            setDisplayPreviousPage(true);
        }
    }, [totalRecords, nocList])

    useEffect(() => {
        setPagination({current: DEFAULT_PAGE, pageSize: pagination.pageSize, displayLastPage});
    }, [search]);

    const showTotalPagination = (total, range) => {
        let showTotal;

        if(range[0] >= 0 && range[1] >= 0){
            if(search === '') {
                showTotal = `Showing ${range[0]}-${range[1]} items. Total ${totalRecords} entries.`;
            } else if(search && totalFilteredResults > 0){
                showTotal = `${totalFilteredResults} result${totalFilteredResults > 1 ? 's' : ''} containing "${search}".`
            } else if (search && !totalFilteredResults){
                showTotal = `No matching results containing "${search}". Results filtered from ${totalRecords} entries.`
            }
        }
        return showTotal;
    }

    return(
        <>
            <div {...props} className={`${props.className || ''} noc__list`}>
                <Col span={24}>
                    { !loadingModal &&  
                        <Title level={5}>All Customers <Text type='secondary'>{(totalRecords)}</Text></Title>
                    }
                </Col>
                <Row justify="space-between">
                    <Col>
                        <Space>
                            <p>Search:</p><Input suffix={<SearchOutlined/>} placeholder="input search text" allowClear onChange={(e) => setSearch(e.target.value)} value={search}/>
                            {search ? <Button type='primary' onClick={()=> setSearch('')}>Clear</Button> : ''}
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<SyncOutlined />}  onClick={() => refreshHandler()}>Refresh</Button>
                            <Restricted to="noc.addCustomer">
                                <Button onClick={()=> {setDisplayAddNewCustomer(true)}} type="primary" icon={<UserAddOutlined />}>Add New Customer</Button>
                            </Restricted>
                        </Space>
                    </Col>
                </Row>

                <NocListHeader/>

                { loadingModal ? 
                    <Skeleton/> 
                    :
                    <> 
                        { nocList && renderNocItem() }

                        { (!totalRecords || (search && !totalFilteredResults)) &&
                            <Empty 
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={totalFilteredResults === 0 ? `0 matching results` : 'No Data'}
                                />
                        }

                        {pagination &&
                            <Row justify="end" style={{marginTop: 20}}>
                                <Pagination 
                                    total={!search ? totalRecords : totalFilteredResults}
                                    current = {pagination.current}
                                    defaultCurrent={DEFAULT_PAGE}
                                    defaultPageSize={DEFAULT_PAGE_SIZE}
                                    showSizeChanger
                                    onShowSizeChange={(page, pageSize) => onShowSizeChange(page, pageSize)}
                                    showQuickJumper
                                    showTotal = {(total, range) => showTotalPagination(total, range)}
                                    onChange={(page, pageSize) => onChange(page, pageSize)}
                                />
                            </Row> 
                        }
                    </>
                }
            </div>

            { displayAddNewCustomer &&
                <>
                { !loadingModal &&
                    <Modal 
                        title="Add New Customer" 
                        visible={displayAddNewCustomer} 
                        setVisible={setDisplayAddNewCustomer} 
                        actionGroup={false}
                        width={700}
                        {...displaySuccessMessage}
                        >
                        <AddNewCustomerForm setDisplayAddNewCustomer={setDisplayAddNewCustomer} setDisplayLastPage={setDisplayLastPage} refreshData={refreshHandler} setDisplaySuccessMessage={setDisplaySuccessMessage} />
                    </Modal>
                }
                </>
            }

            <Spin spinning={loadingModal} fullscreen/>
        </>
    );
};